import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Clock } from 'lucide-react';
import {
  PageContainer,
  PaymentCard as Card,
  PaymentBrandPanel,
  CheckoutPanel,
  LoadingSpinner,
  StatusWrap,
  StatusIcon,
  StatusTitle as Title,
  StatusMessage as Message,
  StatusDetails as DetailBox,
  DetailRow,
  DetailLabel,
  DetailValue,
} from './PaymentVisuals';

const CountdownText = Message;

const RestoPaymentCallback = () => {
  const [searchParams] = useSearchParams();
  const [countdown, setCountdown] = useState(3);
  const [processing, setProcessing] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);

  // Start auto-close countdown and close the popup window
  const startAutoClose = () => {
    const interval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          window.close();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Send result to parent window via postMessage.
  // After cross-origin navigation through PhonePe, window.opener can be null in some
  // browsers. We try multiple targets so at least one reaches the parent.
  const sendResultToParent = (result, parentOrigin) => {
    const targetOrigin = parentOrigin || '*';
    let sent = false;

    // Primary: window.opener (standard popup flow)
    if (window.opener) {
      try {
        window.opener.postMessage(result, targetOrigin);
        sent = true;
        console.log('Sent payment result to opener:', result.status, 'origin:', targetOrigin);
      } catch (e) {
        console.warn('opener.postMessage failed:', e);
      }
    }

    // Fallback: broadcast to '*' so parent can catch it even if opener ref lost
    if (!sent && parentOrigin) {
      try {
        // Try posting to all accessible windows (covers edge cases)
        window.postMessage(result, window.location.origin);
        console.log('Sent payment result via self.postMessage fallback');
      } catch (e) {
        console.warn('self.postMessage fallback failed:', e);
      }
    }

    if (!sent && !window.opener) {
      console.warn('No parent window (opener) available — parent will poll backend directly.');
    }
  };

  useEffect(() => {
    const processCallback = async () => {
      // Get merchantTransactionId from URL
      const merchantTransactionId = searchParams.get('merchantTransactionId') ||
                                    searchParams.get('transactionId');

      if (!merchantTransactionId) {
        console.error('No merchantTransactionId found in URL');
        setProcessing(false);
        startAutoClose();
        return;
      }

      // Get parent origin from localStorage (set before PhonePe redirect)
      const parentOrigin = localStorage.getItem(`payment_origin_${merchantTransactionId}`) ||
                           searchParams.get('origin') ||
                           null;

      // Get stored payment data (set before PhonePe redirect)
      const storedData = localStorage.getItem(`payment_data_${merchantTransactionId}`);
      const paymentData = storedData ? JSON.parse(storedData) : {};

      // Cleanup localStorage regardless of outcome
      localStorage.removeItem(`payment_origin_${merchantTransactionId}`);
      localStorage.removeItem(`payment_data_${merchantTransactionId}`);

      try {
        // Fetch payment status from backend
        const apiUrl = process.env.REACT_APP_API_URL || 'https://api.willovate.in';
        const statusResponse = await fetch(`${apiUrl}/api/payment/phonepe/status/${merchantTransactionId}`);

        if (!statusResponse.ok) {
          throw new Error(`Failzed to fetch payment status (HTTP ${statusResponse.status})`);
        }

        const statusResult = await statusResponse.json();
        console.log('Payment status from backend:', statusResult);

        // Store status for UI display
        setPaymentStatus(statusResult.data);
        setProcessing(false);

        // Map backend status to result status
        // COMPLETED → SUCCESS, PENDING → PENDING, anything else → FAILED
        let status;
        const backendStatus = statusResult.data?.status;
        if (backendStatus === 'COMPLETED') {
          status = 'SUCCESS';
        } else if (backendStatus === 'PENDING') {
          status = 'PENDING';
        } else {
          status = 'FAILED';
        }

        const result = {
          type: 'PAYMENT_RESULT',
          status,
          transactionId: statusResult.data?.transactionId,
          merchantTransactionId,
          amount: statusResult.data?.amount || paymentData.amount,
          message:
            status === 'SUCCESS'
              ? 'Payment completed successfully'
              : status === 'PENDING'
              ? 'Payment is being processed'
              : 'Payment failed or was cancelled',
          paymentMethod: statusResult.data?.paymentMethod,
          ...paymentData, // Include original payment data (planId, orderId, etc.)
        };

        // Send result to parent, then auto-close
        sendResultToParent(result, parentOrigin);
        startAutoClose();
      } catch (error) {
        console.error('Error processing payment callback:', error);

        // Always notify the parent of the failure so it doesn't hang
        const failedResult = {
          type: 'PAYMENT_RESULT',
          status: 'FAILED',
          merchantTransactionId,
          amount: paymentData.amount,
          message: `Payment verification failed: ${error.message}`,
          ...paymentData,
        };
        sendResultToParent(failedResult, parentOrigin);

        setProcessing(false);
        startAutoClose();
      }
    };

    processCallback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const backendStatus = paymentStatus?.status;
  const isSuccess = backendStatus === 'COMPLETED';
  const isPending = backendStatus === 'PENDING';
  const transactionId = paymentStatus?.transactionId;
  const amount = paymentStatus?.amount;

  if (processing) {
    return (
      <PageContainer>
        <Card>
          <PaymentBrandPanel gateway="PhonePe" eyebrow="Payment verification" />
          <CheckoutPanel>
            <StatusWrap>
              <LoadingSpinner />
              <Title>Verifying payment</Title>
              <Message>Please wait while we confirm the transaction with PhonePe.</Message>
            </StatusWrap>
          </CheckoutPanel>
        </Card>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Card>
        <PaymentBrandPanel gateway="PhonePe" eyebrow="Transaction status" />
        <CheckoutPanel>
          <StatusWrap>
            <StatusIcon className={isSuccess ? 'success' : isPending ? 'pending' : 'failed'}>
              {isSuccess ? (
                <CheckCircle size={37} />
              ) : isPending ? (
                <Clock size={37} />
              ) : (
                <XCircle size={37} />
              )}
            </StatusIcon>

            <Title>
              {isSuccess ? 'Payment successful' : isPending ? 'Payment pending' : 'Payment failed'}
            </Title>

            <Message>
              {isSuccess
                ? 'Your payment has been processed successfully.'
                : isPending
                ? 'Your payment is being verified. We will update you once confirmed.'
                : 'Your payment could not be completed. Please try again.'}
            </Message>

            {transactionId && (
              <DetailBox>
                <DetailRow>
                  <DetailLabel>Transaction ID</DetailLabel>
                  <DetailValue>{transactionId}</DetailValue>
                </DetailRow>
                {amount && (
                  <DetailRow>
                    <DetailLabel>Amount</DetailLabel>
                    <DetailValue>₹{Number(amount).toFixed(2)}</DetailValue>
                  </DetailRow>
                )}
                <DetailRow>
                  <DetailLabel>Status</DetailLabel>
                  <DetailValue
                    style={{
                      color: isSuccess ? '#159764' : isPending ? '#d48a16' : '#d8434f',
                    }}
                  >
                    {isSuccess ? 'Success' : isPending ? 'Pending' : 'Failed'}
                  </DetailValue>
                </DetailRow>
                {paymentStatus?.paymentMethod && (
                  <DetailRow>
                    <DetailLabel>Payment Method</DetailLabel>
                    <DetailValue>{paymentStatus.paymentMethod}</DetailValue>
                  </DetailRow>
                )}
              </DetailBox>
            )}

            <CountdownText>
              This window will close automatically in {countdown} second{countdown !== 1 ? 's' : ''}.
            </CountdownText>
          </StatusWrap>
        </CheckoutPanel>
      </Card>
    </PageContainer>
  );
};

export default RestoPaymentCallback;
